import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Textarea,
  VStack,
  HStack,
  Text,
  Box,
  useToast,
  Divider,
  Badge,
  Icon,
  Input,
} from "@chakra-ui/react";
import { getSupabase } from "./supabaseClient";
import { bucketName_files, ADMIN_PASSWORD } from "./env";
import {
  loadGlobalSystemTemplate,
  saveGlobalSystemTemplate,
  hasGlobalSystemTemplate,
} from "../utils/formProcessingUtils";

// Get bucket name with fallback
const getBucketName = () => {
  return bucketName_files &&
    bucketName_files !== "undefined" &&
    bucketName_files !== undefined
    ? bucketName_files
    : "files";
};

/**
 * GlobalSystemPromptManager - A modal for managing global system prompts
 * that apply to all users (including new ones)
 */
const GlobalSystemPromptManager = ({
  isOpen,
  onClose,
  patientType = "followup",
}) => {
  const toast = useToast();

  // Password verification state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [passwordError, setPasswordError] = useState(false);

  // State
  const [templateInfo, setTemplateInfo] = useState("");
  const [promptInfo, setPromptInfo] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasGlobal, setHasGlobal] = useState(false);
  const [originalTemplate, setOriginalTemplate] = useState("");
  const [originalPrompt, setOriginalPrompt] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);

  // Load global template on mount or when modal opens
  useEffect(() => {
    if (isOpen) {
      loadGlobalTemplate();
    }
  }, [isOpen, patientType]);

  // Reset authentication state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsAuthenticated(false);
      setPasswordInput("");
      setPasswordError(false);
    }
  }, [isOpen]);

  const handleVerifyPassword = () => {
    setIsVerifying(true);
    setPasswordError(false);

    // Simple password check
    if (passwordInput === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setIsVerifying(false);
    } else {
      setPasswordError(true);
      setIsVerifying(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleVerifyPassword();
    }
  };

  const loadGlobalTemplate = async () => {
    setIsLoading(true);
    try {
      const globalTemplate = await loadGlobalSystemTemplate(patientType);

      if (globalTemplate.templateText && globalTemplate.promptText) {
        setTemplateInfo(globalTemplate.templateText);
        setPromptInfo(globalTemplate.promptText);
        setOriginalTemplate(globalTemplate.templateText);
        setOriginalPrompt(globalTemplate.promptText);
        setHasGlobal(true);
      } else {
        // Set default values if no global template exists
        const defaults = getDefaultValues(patientType);
        setTemplateInfo(defaults.template);
        setPromptInfo(defaults.prompt);
        setOriginalTemplate(defaults.template);
        setOriginalPrompt(defaults.prompt);
        setHasGlobal(false);
      }
      setIsInitialized(true);
    } catch (error) {
      console.error("Error loading global template:", error);
      const defaults = getDefaultValues(patientType);
      setTemplateInfo(defaults.template);
      setPromptInfo(defaults.prompt);
      setOriginalTemplate(defaults.template);
      setOriginalPrompt(defaults.prompt);
      setHasGlobal(false);
      setIsInitialized(true);
    } finally {
      setIsLoading(false);
    }
  };

  const getDefaultValues = (type) => {
    if (type === "followup") {
      return {
        template: `Appointment Type: {appointment_type}
Recent Appointment Date: {recent_date}
SOAP Notes: {soap_notes}
Pending Follow-ups: {pending_followups}
Form Submissions: {form_submissions}`,
        prompt: `Based on the following patient information, generate a professional follow-up text message:

Patient Name: {patient_name}
Provider Name: {provider_name}
{patient_context}

Generate a detailed, professional follow-up message appropriate for the patient's condition and visit type. The message should:
1. Thank the patient for their visit
2. Mention any important follow-up items or pending results
3. Provide clear next steps
4. Include a friendly closing from {provider_name}

IMPORTANT: Use the exact provider name "{provider_name}" in the message. Do NOT use placeholders.

Generate a detailed message - don't limit the length.

Generate only the message text, no explanations:`,
      };
    }
    // Default for other types
    return {
      template: "",
      prompt: "",
    };
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveGlobalSystemTemplate(patientType, templateInfo, promptInfo);

      setOriginalTemplate(templateInfo);
      setOriginalPrompt(promptInfo);
      setHasGlobal(true);

      toast({
        title: "Global Template Saved",
        description:
          "This template will now be used for all users (including new users)",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      console.error("Error saving global template:", error);
      toast({
        title: "Save Error",
        description: `Failed to save global template: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    // Reset to default values
    const defaults = getDefaultValues(patientType);
    setTemplateInfo(defaults.template);
    setPromptInfo(defaults.prompt);

    toast({
      title: "Reset Complete",
      description: "Template reset to system defaults",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleDelete = async () => {
    setIsSaving(true);
    try {
      // Delete by saving empty content or we could add a delete function
      // For now, we'll save empty to "clear" the global template
      await saveGlobalSystemTemplate(patientType, "", "");

      setHasGlobal(false);
      const defaults = getDefaultValues(patientType);
      setTemplateInfo(defaults.template);
      setPromptInfo(defaults.prompt);
      setOriginalTemplate(defaults.template);
      setOriginalPrompt(defaults.prompt);

      toast({
        title: "Global Template Removed",
        description:
          "Global template has been removed, will use hardcoded defaults",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error deleting global template:", error);
      toast({
        title: "Delete Error",
        description: `Failed to delete global template: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges =
    isInitialized &&
    (templateInfo !== originalTemplate || promptInfo !== originalPrompt);

  const getPatientTypeLabel = (type) => {
    if (type === "followup") return "Follow-up Messages";
    if (type === "NP" || type === "FFP") return "Pulmonary Forms";
    if (type === "NS" || type === "FFS") return "Sleep Forms";
    return "Templates";
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      isCentered
      motionPreset="slideInBottom"
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
      <ModalContent maxH="90vh" overflowY="auto">
        <ModalHeader>
          <VStack align="start" spacing={1}>
            <HStack>
              <Text>Global System Prompt</Text>
              <Badge colorScheme="red" fontSize="sm">
                Admin Only
              </Badge>
            </HStack>
            <Text fontSize="xs" color="gray.500" fontWeight="normal">
              This template applies to ALL users (including new users)
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {/* Password verification screen */}
          {!isAuthenticated ? (
            <VStack spacing={4} py={8}>
              <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                Admin Password Required
              </Text>
              <Text fontSize="sm" color="gray.500" textAlign="center">
                Enter the admin password to manage global system prompts
              </Text>
              <Input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter admin password"
                size="lg"
                width="250px"
                textAlign="center"
              />
              {passwordError && (
                <Text color="red.500" fontSize="sm">
                  Incorrect password. Please try again.
                </Text>
              )}
              <Button
                colorScheme="blue"
                onClick={handleVerifyPassword}
                isLoading={isVerifying}
                mt={2}
              >
                Verify
              </Button>
            </VStack>
          ) : isLoading ? (
            <Box py={8} textAlign="center">
              <Text color="gray.600">Loading global template...</Text>
            </Box>
          ) : (
            <VStack spacing={4} align="stretch">
              {/* Info Banner */}
              <Box
                p={3}
                bg={hasGlobal ? "green.50" : "orange.50"}
                borderRadius="md"
                borderWidth="1px"
                borderColor={hasGlobal ? "green.200" : "orange.200"}
              >
                <HStack justify="space-between">
                  <VStack align="start" spacing={0}>
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      color={hasGlobal ? "green.700" : "orange.700"}
                    >
                      {hasGlobal
                        ? "Global Template Active"
                        : "No Global Template"}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      {hasGlobal
                        ? "All users will use this template instead of hardcoded defaults"
                        : "Currently using hardcoded default templates"}
                    </Text>
                  </VStack>
                  <Badge
                    colorScheme={hasGlobal ? "green" : "gray"}
                    fontSize="sm"
                  >
                    {getPatientTypeLabel(patientType)}
                  </Badge>
                </HStack>
              </Box>

              <Divider />

              {/* System Prompt - Now on top */}
              <Box>
                <Text
                  fontWeight="semibold"
                  fontSize="sm"
                  color="gray.700"
                  mb={2}
                >
                  System Prompt:
                </Text>
                <Textarea
                  value={promptInfo}
                  onChange={(e) => setPromptInfo(e.target.value)}
                  placeholder="Enter system prompt..."
                  rows={8}
                  resize="vertical"
                  fontFamily="mono"
                  fontSize="xs"
                />
              </Box>

              {/* Template Information - Now at bottom */}
              <Box>
                <Text
                  fontWeight="semibold"
                  fontSize="sm"
                  color="gray.700"
                  mb={2}
                >
                  Template Information:
                </Text>
                <Textarea
                  value={templateInfo}
                  onChange={(e) => setTemplateInfo(e.target.value)}
                  placeholder="Enter template information..."
                  rows={5}
                  resize="vertical"
                  fontFamily="mono"
                  fontSize="xs"
                />
              </Box>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          {isAuthenticated && (
            <HStack spacing={3}>
              {hasGlobal && (
                <Button
                  variant="outline"
                  colorScheme="red"
                  onClick={handleDelete}
                  isLoading={isSaving}
                  size="sm"
                >
                  Remove Global
                </Button>
              )}
              <Button
                variant="outline"
                onClick={handleReset}
                isDisabled={isSaving}
                size="sm"
              >
                Reset to Defaults
              </Button>
              <Button
                variant="outline"
                onClick={onClose}
                isDisabled={isSaving}
                size="sm"
              >
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSave}
                isLoading={isSaving}
                isDisabled={
                  !templateInfo.trim() || !promptInfo.trim() || !hasChanges
                }
                size="sm"
              >
                Save Global
              </Button>
            </HStack>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default GlobalSystemPromptManager;
